import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SUB_BRANDS, SERVICE_CATEGORIES } from "../data/content";
import SEO, { breadcrumbSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import ContactForm from "../components/ContactForm";

const BRAND_TO_SERVICE = {
  firesurv: "fire-safety",
  mouldsurv: "damp-mould",
};

export default function SubBrand() {
  const { slug } = useParams();
  const brand = SUB_BRANDS[slug];
  if (!brand) return <Navigate to="/" replace />;
  const primaryService = SERVICE_CATEGORIES.find(s => s.slug === BRAND_TO_SERVICE[slug]);

  const isDark = brand.bg !== "#FFFFFF";
  const textMain = isDark ? "text-white" : "text-slate-900";
  const textMuted = isDark ? "text-white/70" : "text-slate-600";
  const borderCol = isDark ? "border-white/15" : "border-slate-200";

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Brands", path: "/" },
    { name: brand.name, path: `/brands/${slug}` },
  ];

  return (
    <div data-testid={`page-brand-${slug}`} style={{ background: brand.bg }}>
      <SEO
        title={`${brand.name} — ${brand.tagline} | Oxford Environmental Consultants`}
        description={brand.description}
        path={`/brands/${slug}`}
        schema={breadcrumbSchema(crumbs)}
      />
      <section className="pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className={`text-[10px] uppercase tracking-[0.3em] mb-6 ${isDark ? "text-white/50" : "text-slate-500"}`}>Oxford Environmental Consultants · Trading Brand</p>
            <h1 className={`font-display text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05] ${textMain}`}>{brand.name}</h1>
            <p className={`mt-6 text-xl lg:text-2xl ${textMuted}`}>{brand.tagline}</p>
            <p className={`mt-8 text-base lg:text-lg max-w-2xl leading-relaxed ${textMuted}`}>{brand.description}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              {primaryService && (
                <Link to={`/services/${primaryService.slug}`} className="gs-gradient text-white font-semibold px-8 py-4 flex items-center gap-2 hover:opacity-90" data-testid="brand-cta-service">
                  View services <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
              <Link to="/contact" className={`border ${isDark ? "border-white/30 text-white hover:bg-white hover:text-[#0A0F1C]" : "border-slate-300 text-slate-900 hover:bg-slate-900 hover:text-white"} font-semibold px-8 py-4 transition-colors`} data-testid="brand-cta-contact">
                Request a quote
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white border-2 border-slate-200 p-12 flex items-center justify-center min-h-[280px]">
              <img src={brand.logo} alt={brand.name} loading="lazy" decoding="async" width="300" height="200" className="max-h-32 w-auto object-contain" />
            </div>
          </div>
        </div>
      </section>

      {primaryService && (
        <section className={`py-20 lg:py-28 ${isDark ? "bg-black/40" : "bg-[#F8FAFC]"}`}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mb-14">
              <p className={`text-[10px] uppercase tracking-[0.3em] mb-4 ${isDark ? "text-white/50" : "text-slate-500"}`}>Core service offering</p>
              <h2 className={`font-display text-4xl lg:text-5xl font-bold tracking-tight ${textMain}`}>{primaryService.name}</h2>
              <p className={`mt-4 text-lg ${textMuted}`}>{primaryService.summary}</p>
              <p className={`mt-2 text-[10px] uppercase tracking-[0.2em] ${isDark ? "text-white/40" : "text-slate-500"}`}>{primaryService.legislation}</p>
            </div>
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px ${isDark ? "bg-white/15" : "bg-slate-200"} border ${borderCol}`}>
              {primaryService.items.map((it, i) => (
                <div key={i} className={`${isDark ? "bg-[#0A0F1C]" : "bg-white"} p-8 min-h-[180px]`}>
                  <p className={`font-display text-xl font-semibold mb-3 ${textMain}`}>{it.name}</p>
                  <p className={`text-sm leading-relaxed ${textMuted}`}>{it.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={`py-20 ${isDark ? "" : "bg-white"}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className={`font-display text-4xl font-bold tracking-tight ${textMain}`}>Talk to {brand.name}</h2>
            <p className={`mt-4 leading-relaxed ${textMuted}`}>Your enquiry is routed directly to the specialist team within the {brand.name} division.</p>
          </div>
          <div className="lg:col-span-7">
            <ContactForm defaultService={primaryService?.name || "General Enquiry"} variant={isDark ? "dark" : "light"} />
          </div>
        </div>
      </section>

      <section className={`${isDark ? "border-t border-white/10" : "bg-[#F8FAFC] border-t border-slate-200"} py-16`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className={`text-[10px] uppercase tracking-[0.3em] mb-6 ${isDark ? "text-white/50" : "text-slate-500"}`}>Part of the Oxford Environmental Consultants</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.values(SUB_BRANDS).filter(b => b.slug !== slug).map((b) => (
              <a key={b.slug} href={b.website} target="_blank" rel="noreferrer" className={`border ${borderCol} p-6 hover:opacity-80 transition-opacity`} data-testid={`brand-link-${b.slug}`}>
                <p className={`font-display text-xl font-semibold ${textMain}`}>{b.name}</p>
                <p className={`text-sm mt-1 ${textMuted}`}>{b.tagline}</p>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
